import { useMemo } from 'react'
import { Bell, Bot, CalendarClock, CheckCheck, ClipboardCheck, Video } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'
import { useMutation } from '@tanstack/react-query'

import { cn } from '@/custolink-newdesign/lib/utils'
import { Button } from '@/custolink-newdesign/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/custolink-newdesign/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/custolink-newdesign/ui/dropdown-menu'
import { adminAreaNotifications } from '@/custolink-state/ui_admin_area_notifications'
import {
  AdmUserNotificationsApi,
  AdmUserNotificationsApi$LatestNotification,
  AdmUserNotificationsApi$LatestNotificationType,
} from '@/custolink-transpiled_by_server/bc.identity_and_access.api_methods'

/**
 * New-design visuals for custolink-components/navigation/Notification/Notification.tsx.
 *
 * Same atom (adminAreaNotifications, filled every 30s by AdminArea.tsx) and the
 * same two mutations (markAsRead, markAllAsRead, both AdmUserNotificationsApi)
 * as the component this replaces - only the markup changes: a DropdownMenu
 * instead of the Ecme Dropdown/ScrollBar, lucide icons instead of react-icons,
 * and the new design's Button primitive for the mark-all-as-read action.
 */

/** flow has no icon for these; same four icons as the old NotificationAvatar, swapped for lucide. */
const NOTIFICATION_ICON: Partial<Record<AdmUserNotificationsApi$LatestNotificationType, typeof Bell>> = {
  [AdmUserNotificationsApi$LatestNotificationType.AssetAiConversation]: Bot,
  [AdmUserNotificationsApi$LatestNotificationType.AssetVideoCall]: Video,
  [AdmUserNotificationsApi$LatestNotificationType.ProductRegistered]: ClipboardCheck,
  [AdmUserNotificationsApi$LatestNotificationType.Inquiry]: CalendarClock,
}

/** Same copy as the old NotificationEntry/TextOfType - custolink-components/navigation/Notification/Notification.tsx:180-215. */
function notificationCopy(type: AdmUserNotificationsApi$LatestNotificationType) {
  switch (type) {
    case AdmUserNotificationsApi$LatestNotificationType.AssetAiConversation:
      return { subject: 'Ai Conversation', verb: 'was started by a customer' }
    case AdmUserNotificationsApi$LatestNotificationType.AssetVideoCall:
      return { subject: 'Video Support', verb: 'was requested by a customer' }
    case AdmUserNotificationsApi$LatestNotificationType.ProductRegistered:
      return { subject: 'Product Registration', verb: 'was submitted by a customer' }
    case AdmUserNotificationsApi$LatestNotificationType.Inquiry:
      return { subject: 'Inquiry', verb: 'was submitted by a customer' }
    default:
      return null
  }
}

/** Every notification type links to the support hub - same component, :136-143. */
const NOTIFICATION_TARGET = '/admin/communication/support'

function NotificationRow({
  item,
  onRead,
}: {
  item: AdmUserNotificationsApi$LatestNotification
  onRead: () => void
}) {
  const navigate = useNavigate()
  const Icon = NOTIFICATION_ICON[item.type]
  const copy = notificationCopy(item.type)

  return (
    <DropdownMenuItem
      className="cl:relative cl:items-start cl:gap-3 cl:py-2.5 cl:pr-6"
      onSelect={() => {
        // Mark as read on click, then navigate - same order as the old onClick.
        onRead()
        navigate(NOTIFICATION_TARGET)
      }}
    >
      <span className="cl:bg-muted cl:text-muted-foreground cl:flex cl:size-8 cl:shrink-0 cl:items-center cl:justify-center cl:rounded-full">
        {Icon ? <Icon className="cl:size-4" /> : null}
      </span>
      <span className="cl:flex cl:min-w-0 cl:flex-col cl:gap-0.5">
        {copy ? (
          <span className="cl:text-sm cl:leading-snug">
            <span className="cl:font-semibold">{copy.subject}</span> {copy.verb}
          </span>
        ) : null}
        <span className="cl:text-muted-foreground cl:text-xs">{item.createdPrettyDate}</span>
      </span>
      <span
        aria-hidden
        className={cn(
          'cl:absolute cl:top-3 cl:right-2 cl:size-2 cl:shrink-0 cl:rounded-full',
          item.isRead ? 'cl:bg-muted-foreground/30' : 'cl:bg-primary'
        )}
      />
    </DropdownMenuItem>
  )
}

export default function NotificationsMenu() {
  const [notifications, setNotifications] = useAtom(adminAreaNotifications)

  const markAsRead = useMutation({
    mutationFn: (p: { notificationId: number }) =>
      AdmUserNotificationsApi.markAsRead(p.notificationId),
    onSuccess: (_, p: { notificationId: number }) => {
      setNotifications((s) => ({
        ...s,
        latest10: s.latest10.map((n) =>
          n.id === p.notificationId ? { ...n, isRead: true } : n
        ),
      }))
    },
  })

  const markAllAsRead = useMutation({
    mutationFn: () =>
      AdmUserNotificationsApi.markMultipleAsRead({
        ids: notifications.latest10.map((n) => n.id),
      }),
    onSuccess: () => {
      setNotifications((s) => ({
        ...s,
        latest10: s.latest10.map((n) => ({ ...n, isRead: true })),
      }))
    },
  })

  const hasUnread = useMemo(() => notifications.latest10.some((n) => !n.isRead), [notifications])

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Notifications"
              className="cl:relative cl:text-muted-foreground cl:hover:text-foreground cl:size-9 cl:md:size-8"
            >
              <Bell />
              {hasUnread ? (
                <span
                  aria-hidden
                  className="cl:bg-primary cl:ring-background cl:absolute cl:top-1.5 cl:right-1.5 cl:size-2 cl:rounded-full cl:ring-2"
                />
              ) : null}
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent>Notifications</TooltipContent>
      </Tooltip>

      <DropdownMenuContent align="end" className="cl:w-80 cl:p-0">
        <div className="cl:flex cl:items-center cl:justify-between cl:gap-2 cl:border-b cl:px-3 cl:py-2.5">
          <span className="cl:text-sm cl:font-semibold">Notifications</span>
          {hasUnread ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Mark all as read"
                  onClick={() => markAllAsRead.mutate()}
                >
                  <CheckCheck />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Mark all as read</TooltipContent>
            </Tooltip>
          ) : null}
        </div>

        <div className="cl:max-h-80 cl:overflow-y-auto cl:py-1">
          {notifications.latest10.length > 0 ? (
            notifications.latest10.map((item) => (
              <NotificationRow
                key={item.id}
                item={item}
                onRead={() => markAsRead.mutate({ notificationId: item.id })}
              />
            ))
          ) : (
            <div className="cl:flex cl:flex-col cl:items-center cl:gap-1 cl:px-4 cl:py-10 cl:text-center">
              <span className="cl:text-sm cl:font-semibold">No notifications!</span>
              <span className="cl:text-muted-foreground cl:text-xs">Please try again later.</span>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
